<template>
  <div class="row dashboard-page">
    <!-- ===================== Welcome Header ===================== -->
    <div class="col-12 c">
      <div class="welcome-header">
        <div class="row">
          <div class="col-md-8">
            <h3 class="mb-1">مرحباً، {{ user.name || 'أستاذنا الفاضل' }} 👋</h3>
            <p class="mb-0">لوحة تحكم ملف الإنجاز — تابع تقدّمك وجميع ملفاتك من مكان واحد</p>
          </div>
          <div class="col-md-4 text-md-left text-center">
            <div v-if="stats.exdate" class="sub-info">
              <i class="fa fa-calendar"></i>
              اشتراكك حتى: <strong dir="ltr">{{ stats.exdate }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== Stats Cards ===================== -->
    <div class="col-12 c" v-if="!loading">
      <div class="row">
        <div class="col-6 col-md-3">
          <div class="stat-card blue">
            <i class="fa fa-folder-open"></i>
            <div class="number">{{ stats.totalFiles || 0 }}</div>
            <div class="label">إجمالي الملفات</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="stat-card green">
            <i class="fa fa-check-circle"></i>
            <div class="number">{{ stats.avgCompletion || 0 }}%</div>
            <div class="label">متوسط الإنجاز</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="stat-card orange">
            <i class="fa fa-list"></i>
            <div class="number">{{ stats.completedTasks || 0 }}</div>
            <div class="label">شواهد منجزة</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="stat-card purple">
            <i class="fa fa-image"></i>
            <div class="number">{{ stats.totalImages || 0 }}</div>
            <div class="label">صور ومرفقات</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== Sub Stats ===================== -->
    <div class="col-12 c" v-if="!loading && stats.totalFiles > 0">
      <div class="row">
        <div class="col-md-4">
          <div class="sub-card">
            <div class="sub-card-icon" style="background: #dbeafe; color: #1e40af;">
              <i class="fa fa-eye"></i>
            </div>
            <div>
              <div class="sub-card-num">{{ stats.reviewedFiles || 0 }}</div>
              <div class="sub-card-label">ملفات تمت مراجعتها</div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="sub-card">
            <div class="sub-card-icon" style="background: #d1fae5; color: #065f46;">
              <i class="fa fa-thumbs-up"></i>
            </div>
            <div>
              <div class="sub-card-num">{{ stats.approvedFiles || 0 }}</div>
              <div class="sub-card-label">ملفات معتمدة</div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="sub-card">
            <div class="sub-card-icon" style="background: #faf5ff; color: #9112BC;">
              <i class="fa fa-pencil"></i>
            </div>
            <div>
              <div class="sub-card-num">{{ stats.signedFiles || 0 }}</div>
              <div class="sub-card-label">ملفات موقّعة</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== Quick Actions ===================== -->
    <div class="col-12 c">
      <div class="row">
        <div class="col-6 col-lg-6">
          <div class="action-card red-grad" @click="$router.push('/create')">
            <i class="fa fa-plus-circle"></i>
            <div>
              <strong>إنشاء ملف إنجاز جديد</strong>
              <p>ابدأ ملف إنجاز جديد ونظّم شواهدك</p>
            </div>
          </div>
        </div>
        <div class="col-6 col-lg-6">
          <div class="action-card blue-grad" @click="$router.push('/list')">
            <i class="fa fa-folder"></i>
            <div>
              <strong>ملفاتي السابقة</strong>
              <p>اطّلع على جميع ملفاتك السابقة وعدّلها</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== Recent Files ===================== -->
    <div class="col-12 c" v-if="!loading && recentFiles.length">
      <div class="card card-body">
        <h5><i class="fa fa-clock-o"></i> آخر الملفات</h5>
        <div class="recent-file" v-for="f in recentFiles" :key="f._id" @click="$router.push('/file/' + f._id)">
          <div class="row">
            <div class="col-md-5">
              <strong>{{ f.title }}</strong>
              <br>
              <small class="text-muted">{{ typeLabel(f.type) }} • آخر تحديث: {{ f.last_update || f.create_date }}</small>
            </div>
            <div class="col-md-5">
              <div class="progress-wrap">
                <div class="progress-bar-bg">
                  <div class="progress-bar-fill" :style="{ width: f.percent + '%', background: percentColor(f.percent) }"></div>
                </div>
                <small>{{ f.done }} من {{ f.total }} ({{ f.percent }}%)</small>
              </div>
            </div>
            <div class="col-md-2 text-center">
              <span class="status-badge" :class="'status-' + f.status">{{ statusLabel(f.status) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== Empty State ===================== -->
    <div class="col-12 c" v-if="!loading && stats.totalFiles == 0">
      <div class="card card-body text-center empty-state">
        <img :src="require('@/assets/images/create.png')" style="width: 120px; margin: 0 auto;">
        <h4>ابدأ ملف الإنجاز الأول</h4>
        <p>لم تقم بإنشاء أي ملف بعد. اضغط على "إنشاء ملف إنجاز جديد" لتبدأ.</p>
      </div>
    </div>

    <!-- ===================== Notices ===================== -->
    <div class="col-12 col-lg-10 c">
      <div class="card card-body notices-card">
        <div class="d-flex justify-content-between">
          <h5><i class="fa fa-info-circle text-warning"></i> تنبيهات هامة</h5>
          <button class="btn btn-sm btn-outline-secondary" @click="showNotices = !showNotices">
            {{ showNotices ? 'إخفاء' : 'عرض' }}
          </button>
        </div>
        <ul v-if="showNotices">
          <li>هذا الموقع هو خدمة لرفع الشواهد وهو بمثابة ملف إنجاز وليس تقييماً نهائياً أو ملزماً لمدير المدرسة.</li>
          <li>عند إنشاء ملف شواهد لا يحتاج أن تكتب اسمك، اكتب مثال: ملف الإنجاز لعام ١٤٤٧ هـ.</li>
          <li>عند حذف الملف سيتم إرسال كود تحقق إلى جوالك المسجّل لضمان تأكيد الحذف.</li>
          <li>الملف يبقى لمدة عام كامل وسيتم حذف ملفات الشواهد بداية كل عام دراسي.</li>
          <li>قم بحفظ وتصدير الملف بعد الانتهاء من إعداده.</li>
          <li>يمكن إضافة صور فقط كمرفقات للشواهد.</li>
        </ul>
      </div>
    </div>

    <!-- ===================== Loading ===================== -->
    <div class="col-12 c" v-if="loading">
      <div class="text-center g">
        <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data(){
    return {
      loading: true,
      user: JSON.parse(localStorage.getItem('user') || '{}'),
      stats: {},
      recentFiles: [],
      showNotices: false,
      typesMap: {
        '1': 'معلم',
        '2': 'معلم - نشاط طلابي',
        '3': 'معلم - توجيه صحي',
        '4': 'محضر مختبر',
        '5': 'معلمة رياض أطفال',
        '6': 'وكيل / مدير',
        '7': 'موجه طلابي'
      },
      statusMap: {
        'pending': 'قيد المراجعة',
        'reviewed': 'تمت المراجعة',
        'approved': 'معتمد',
        'needs_revision': 'يحتاج تعديل'
      }
    }
  },
  created(){
    var g = this;
    $.post(api + '/user/web/dashboard', { jwt: g.user.jwt })
      .then(function(r){
        if(r.status != 100){ g.loading = false; return; }
        g.stats = Object.assign({}, r.response.stats, {
          exdate: r.response.user.exdate
        });
        g.recentFiles = r.response.recentFiles || [];
        g.loading = false;
      })
      .fail(function(){
        g.loading = false;
      });
  },
  methods: {
    typeLabel(t){ return this.typesMap[t] || t; },
    statusLabel(s){ return this.statusMap[s] || 'قيد المراجعة'; },
    percentColor(p){
      if(p >= 75){ return '#10b981'; }
      if(p >= 50){ return '#f59e0b'; }
      return '#ef4444';
    }
  }
}
</script>

<style scoped>
.dashboard-page { padding: 10px 0; }

.welcome-header {
  background: linear-gradient(135deg, #0BA6DF 0%, #6D94C5 50%, #9112BC 100%);
  color: #fff;
  padding: 25px 30px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
.welcome-header h3 { color: #fff; font-weight: 700; margin: 0; }
.welcome-header p { opacity: 0.95; margin-top: 5px; }
.sub-info {
  background: rgba(255,255,255,0.25);
  padding: 8px 15px;
  border-radius: 25px;
  display: inline-block;
  margin-top: 5px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 15px;
  text-align: center;
  border: 2px solid #e2e8f0;
  margin-bottom: 15px;
  transition: transform 0.2s;
}
.stat-card:hover { transform: translateY(-3px); }
.stat-card i { font-size: 28px; margin-bottom: 8px; }
.stat-card .number { font-size: 30px; font-weight: 700; color: #1e293b; margin: 5px 0; }
.stat-card .label { font-size: 12px; color: #64748b; font-weight: 500; }

.stat-card.blue { border-color: #0BA6DF; }
.stat-card.blue i { color: #0BA6DF; }
.stat-card.green { border-color: #10b981; }
.stat-card.green i { color: #10b981; }
.stat-card.orange { border-color: #f59e0b; }
.stat-card.orange i { color: #f59e0b; }
.stat-card.purple { border-color: #9112BC; }
.stat-card.purple i { color: #9112BC; }

.sub-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.sub-card-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.sub-card-num { font-size: 22px; font-weight: 700; color: #1e293b; }
.sub-card-label { font-size: 12px; color: #64748b; }

.action-card {
  border-radius: 15px;
  padding: 25px 20px;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}
.action-card:hover { transform: translateY(-3px); }
.action-card i { font-size: 40px; }
.action-card strong { font-size: 18px; color: #fff; display: block; }
.action-card p { margin: 3px 0 0 0; opacity: 0.95; font-size: 12px; }
.red-grad { background: linear-gradient(135deg, #ef4444, #dc2626); }
.blue-grad { background: linear-gradient(135deg, #0BA6DF, #0284c7); }

.recent-file {
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  margin-bottom: 8px;
  cursor: pointer;
  transition: background 0.2s;
}
.recent-file:hover { background: #f8fafc; }

.progress-wrap { padding-top: 5px; }
.progress-bar-bg {
  background: #e2e8f0;
  border-radius: 10px;
  height: 10px;
  overflow: hidden;
  margin-bottom: 4px;
}
.progress-bar-fill { height: 100%; border-radius: 10px; transition: width 0.3s; }

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 11px;
  font-weight: 600;
}
.status-pending { background: #fef3c7; color: #92400e; }
.status-reviewed { background: #dbeafe; color: #1e40af; }
.status-approved { background: #d1fae5; color: #065f46; }
.status-needs_revision { background: #fee2e2; color: #991b1b; }

.empty-state { padding: 40px; }
.empty-state h4 { color: #0BA6DF; margin-top: 15px; }
.empty-state p { color: #64748b; }

.notices-card { background: #fffbeb; border: 1px solid #fde68a; }
.notices-card ul { margin-bottom: 0; padding-right: 20px; }
.notices-card ul li { margin: 5px 0; color: #78350f; }

@media (max-width: 768px){
  .welcome-header { padding: 20px 15px; text-align: center; }
  .welcome-header h3 { font-size: 18px; }
}
</style>
